import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  queryHelpers,
  styled,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import {
  EVENT_NAME,
  trackAnalyticsEvent,
  usePageViewAnalytics,
} from "@leagueplatform/analytics";
import { useIntl } from "@leagueplatform/locales";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { buildHraUrl } from "common/build-hra-url";
import {
  ANALYTICS_SCREEN_NAMES,
  DETAIL,
  PRODUCT_AREA_NAMES,
  SECONDARY_CONTENT_MAX_WIDTH,
  SUB_PRODUCT_AREA_NAMES,
} from "common/constants";
import { OnboardingFlowContainer } from "components/onboarding-flow-container/onboarding-flow-container.component";
import { useHraInfo } from "hooks/use-hra-info/use-hra-info.hook";
import ActionCompleteImage from "assets/action-complete-image.svg";

const FullScreenContainer = styled(StackLayout, {
  width: "100vw",
  height: "100dvh",
  position: "fixed",
  top: "0",
  left: "0",
  zIndex: "$sticky",
  overflowY: "auto",
});

type Props = {
  onComplete: () => Promise<void> | void;
};

// Primary button starts the Health Risk Assessment — destination URL is
// derived from get_app_init_details and the user's selected K-ID (see
// hooks/use-hra-info). Secondary skips. Both finalize FTUX server-side
// regardless of which branch the user takes.
export const OnboardingCompletePage = ({ onComplete }: Props) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));
  const { hraInfo, isLoading: isHraLoading } = useHraInfo();
  const [isSubmitting, setIsSubmitting] = useState(false);

  usePageViewAnalytics({
    product_area: PRODUCT_AREA_NAMES.FOUNDATION,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
    screen_name: ANALYTICS_SCREEN_NAMES.COMPLETE_ONBOARDING,
  });

  const finish = useCallback(
    async (destination: string, detail: string) => {
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: PRODUCT_AREA_NAMES.FOUNDATION,
        sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
        screen_name: ANALYTICS_SCREEN_NAMES.COMPLETE_ONBOARDING,
        detail,
      });
      setIsSubmitting(true);
      try {
        await Promise.resolve(onComplete());
        navigate(destination);
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, onComplete]
  );

  const onStartAssessment = () => {
    const destination = hraInfo
      ? buildHraUrl(hraInfo)
      : APP_MANAGED_ROUTES.HOME;
    finish(destination, DETAIL.START_HEALTH_ASSESSMENT);
  };

  const onSkip = () =>
    finish(APP_MANAGED_ROUTES.HOME, DETAIL.SKIP_HEALTH_ASSESSMENT);

  return (
    <FullScreenContainer
      css={{
        background: "$surfaceBackgroundPrimary",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <OnboardingFlowContainer
        isMobile={isMobile}
        css={{
          maxWidth: SECONDARY_CONTENT_MAX_WIDTH,
          width: "100%",
          margin: "auto",
        }}
      >
        <StackLayout
          horizontalAlignment="center"
          verticalAlignment="center"
          css={{ maxWidth: "468px", margin: "auto" }}
        >
          <img
            src={ActionCompleteImage}
            height="176"
            width="176"
            alt={formatMessage({ id: "STR_ALL_SET_TITLE" })}
          />
          <StackItem
            horizontalAlignment="center"
            css={{
              marginBlock: "$twoAndHalf",
              textAlign: "center",
              gap: "$half",
            }}
          >
            <HeadingText level="3" size="lg">
              {formatMessage({ id: "STR_ALL_SET_TITLE" })}
            </HeadingText>
            <ParagraphText
              css={{
                typography: "$bodyOne",
                color: "$onSurfaceTextSubdued",
              }}
            >
              {formatMessage({ id: "STR_ALL_SET_BODY" })}
            </ParagraphText>
          </StackItem>
          {isSubmitting ? (
            <StackItem css={{ height: "124px", position: "relative" }}>
              <LoadingIndicator />
            </StackItem>
          ) : (
            <StackItem css={{ gap: "$oneAndHalf", width: "100%" }}>
              <Button
                width="fillContainer"
                onClick={onStartAssessment}
                disabled={isHraLoading}
                loading={isHraLoading}
              >
                {formatMessage({ id: "STR_START_HEALTH_ASSESSMENT" })}
              </Button>
              <Button
                width="fillContainer"
                priority="tertiary"
                onClick={onSkip}
              >
                {formatMessage({ id: "STR_NOT_NOW" })}
              </Button>
            </StackItem>
          )}
        </StackLayout>
      </OnboardingFlowContainer>
    </FullScreenContainer>
  );
};
